import { IRecordOf, Record } from '@opengov/compose-app/immutable';
import { IStory } from '@opengov/stories-shared/types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { clamp, pickBy } from 'lodash';
import React, { useState } from 'react';

import { IModalMessages, IStoryEditorContentSpacing } from '../../../../types';
import { STORY_DEFAULT_PADDING_PIXELS } from '../../../constants';
import { styles } from './SpacingMenu.styles';

const MINIMUM_PADDING = 0;
const MAXIMUM_PADDING = 1000;

function parseNumber(stringValue: string): number | undefined {
  const floatValue = parseFloat(stringValue.replace(/[$¢€,]+/g, ''));

  if (isNaN(floatValue)) {
    return undefined;
  }

  return clamp(floatValue, MINIMUM_PADDING, MAXIMUM_PADDING);
}

type IStoryEditorContentSpacingRecord = IRecordOf<IStoryEditorContentSpacing>;

const StoryEditorContentSpacingRecord = Record<IStoryEditorContentSpacing>({
  paddingTop: undefined,
  paddingBottom: undefined,
  breakBefore: undefined
});

export type ISpacingMenu = {
  canEditPageBreaks: boolean;
  story: IStory;
  initialSpacing: IStoryEditorContentSpacing;
  onChangeSpacing(spacing: IStoryEditorContentSpacing): void;
  onCancel(): void;
  messages: IModalMessages;
};

export function SpacingMenu({ story, canEditPageBreaks, initialSpacing, onChangeSpacing, onCancel, messages }: ISpacingMenu) {
  const [spacing, setSpacing] = useState<IStoryEditorContentSpacingRecord>(
    StoryEditorContentSpacingRecord(initialSpacing)
  );

  function handleResetToDefault() {
    setSpacing(StoryEditorContentSpacingRecord());
  }

  function handleCancel() {
    setSpacing(StoryEditorContentSpacingRecord(initialSpacing));
    onCancel();
  }

  function handleSave() {
    onChangeSpacing(pickBy(spacing.toJS(), x => x !== undefined));
  }

  return (
    <Box sx={styles.container}>
      <Typography variant="h6">{messages.spacing}</Typography>
      <Box sx={styles.formRow}>
        <Box sx={styles.fieldGroup}>
          <TextField
            size="small"
            type="number"
            label={messages.topPadding}
            placeholder={String(story.paddingTop ?? STORY_DEFAULT_PADDING_PIXELS)}
            value={spacing.paddingTop !== undefined ? spacing.paddingTop : ''}
            inputProps={{ min: MINIMUM_PADDING, max: MAXIMUM_PADDING, 'data-test': 'story-spacing-top-padding', className: 'no-format' }}
            InputProps={{
              endAdornment: <InputAdornment position="end">{messages.unitPixels}</InputAdornment>
            }}
            onChange={(e) => setSpacing(spacing.set('paddingTop', parseNumber(e.target.value)))}
            sx={styles.numberInput}
          />
        </Box>
        <Box sx={styles.fieldGroup}>
          <TextField
            size="small"
            type="number"
            label={messages.bottomPadding}
            placeholder={String(story.paddingBottom ?? STORY_DEFAULT_PADDING_PIXELS)}
            value={spacing.paddingBottom !== undefined ? spacing.paddingBottom : ''}
            inputProps={{ min: MINIMUM_PADDING, max: MAXIMUM_PADDING, 'data-test': 'story-spacing-bottom-padding', className: 'no-format' }}
            InputProps={{
              endAdornment: <InputAdornment position="end">{messages.unitPixels}</InputAdornment>
            }}
            onChange={(e) => setSpacing(spacing.set('paddingBottom', parseNumber(e.target.value)))}
            sx={styles.numberInput}
          />
        </Box>
      </Box>
      {canEditPageBreaks && (
        <FormControlLabel
          sx={styles.checkbox}
          control={
            <Checkbox
              checked={!!spacing.breakBefore}
              onChange={() => setSpacing(spacing.set('breakBefore', !spacing.breakBefore))}
              inputProps={{ 'data-test': 'story-spacing-add-page-break', className: 'no-format' } as React.InputHTMLAttributes<HTMLInputElement>}
              size="small"
            />
          }
          label={messages.addPageBreak}
        />
      )}
      <Box sx={styles.actionsRow}>
        <Button variant="text" size="small" data-test="story-spacing-reset" onClick={handleResetToDefault}>
          {messages.resetToDefault}
        </Button>
        <Box sx={styles.saveActions}>
          <Button variant="outlined" color="secondary" size="small" data-test="story-spacing-cancel" onClick={handleCancel}>
            {messages.cancel}
          </Button>
          <Button variant="contained" color="primary" size="small" data-test="story-spacing-save" onClick={handleSave}>
            {messages.save}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
